import { classNames } from '@/lib/utils';

/**
 * Chips de ids de catálogo (puestos, zonas) pintados con su etiqueta.
 *
 * Si un id ya no está en el catálogo —alguien lo borró de Configuración
 * después de asignarlo— se pinta el id crudo en vez de desaparecer: que la
 * ficha diga «socorrista» sin traducir es raro, pero perder el dato es peor.
 */
interface ListaProps {
  ids: string[];
  labels: Record<string, string>;
  vacio?: string;
  className?: string;
}

export function ChipsCatalogo({ ids, labels, vacio = '—', className }: ListaProps) {
  if (!ids?.length) return <span className="text-sereno-dark/40">{vacio}</span>;

  return (
    <span className={classNames('inline-flex flex-wrap gap-1', className)}>
      {ids.map((id) => (
        <span
          key={id}
          className="inline-flex items-center px-2 py-0.5 rounded-md bg-sereno-blue/50 text-sereno-dark text-xs font-medium"
        >
          {labels[id] ?? id}
        </span>
      ))}
    </span>
  );
}

/**
 * Selector múltiple hecho de chips pulsables.
 *
 * En el móvil, que es desde donde se dan de alta la mayoría de los candidatos,
 * un `<select multiple>` es inservible: no se ve lo elegido sin desplegarlo y
 * hay que acertar con el dedo en una línea de dieciséis píxeles. Con chips, lo
 * seleccionado se lee de un vistazo y cada objetivo mide 32 px.
 */
interface SelectorProps {
  opciones: { id: string; label: string }[];
  seleccionados: string[];
  onChange: (ids: string[]) => void;
  vacio?: string;
}

export function ChipsSeleccionables({ opciones, seleccionados, onChange, vacio }: SelectorProps) {
  if (!opciones.length) {
    return <p className="text-sm text-sereno-dark/40">{vacio || 'No hay opciones configuradas.'}</p>;
  }

  const alternar = (id: string) => {
    onChange(
      seleccionados.includes(id) ? seleccionados.filter((s) => s !== id) : [...seleccionados, id],
    );
  };

  return (
    <div className="flex flex-wrap gap-2">
      {opciones.map((o) => {
        const activo = seleccionados.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => alternar(o.id)}
            aria-pressed={activo}
            className={classNames(
              'min-h-[32px] px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors',
              activo
                ? 'border-sereno-dark bg-sereno-dark text-white'
                : 'border-gray-200 bg-white text-sereno-dark/70 hover:border-sereno-dark/40',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
